import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ApiClientError,
  FeedbackScreenImprovementSchema,
  type FeedbackCreate,
} from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { clearDraft, loadDraft, saveDraft } from '@/lib/feedback/feedbackDraftStorage';
import { captureScreenshot } from '@/lib/screenshot/captureScreenshot';
import { genUuid } from '@/lib/uuid';
import { FeedbackFormView, type FeedbackFormSubmitState } from './FeedbackFormView';
import type { FeedbackTag } from './feedbackTags';

/**
 * Container Экрана 2.10.1 «Что улучшить в сервисе?» — Block 10 Phase 4.
 *
 * **ADR-0005 (Container/Presenter):** этот файл держит state + side effects
 * (useEffect draft load/save debounced, photo + screenshot lifecycle, submit
 * handler); UI — pure presenter `FeedbackFormView`.
 *
 * **Draft persistence (ADR-0018 §Q4):** localStorage черновик через Phase 2
 * helpers. Persist'им ТОЛЬКО bodyMd / tags / customTag — P-009 запрещает
 * сохранять R2 keys / screenshot (см. JSDoc `feedbackDraftStorage.ts`).
 *
 * **Photo upload (Phase 4 Step 2):**
 *   - До 3 фото (MIME jpg/png/webp, size ≤5MB).
 *   - Каждое фото получает mock R2 key `mock-feedback-photo-<uuid>.<ext>`
 *     для совместимости с MSW handler (Phase 9+ заменит на real R2 upload).
 *   - `URL.createObjectURL` для preview, `URL.revokeObjectURL` при remove +
 *     unmount cleanup (memory leak prevention).
 *
 * **Screenshot opt-in (ADR-0018 §Q3 / P-009):**
 *   - Чекбокс «Прикрепить скриншот» — захват только при `consented: true`.
 *   - `captureScreenshot()` lazy-импортирует html2canvas (Phase 2 wrapper).
 *   - Mock R2 key `mock-feedback-screenshot-<uuid>.png`.
 *   - `URL.createObjectURL`/`revokeObjectURL` при toggle + unmount.
 *
 * **Route context (ADR-0018 §Q5):** `blockId` / `screenId` из `useLocation().state`
 * (передаются BottomBar Phase 3); fallback `'unknown'` при null state.
 *
 * **Submit flow (P-021 5 states):**
 *   - idle / loading / error (success → navigate, view unmounts).
 *   - 'empty' N/A для формы.
 */

const DRAFT_SAVE_DEBOUNCE_MS = 1000;
const MAX_PHOTOS = 3;
const MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED_PHOTO_MIME = ['image/jpeg', 'image/png', 'image/webp'] as const;

export interface AttachedPhoto {
  blob: Blob;
  mockKey: string;
  previewUrl: string;
}

function getExtensionFromMime(mime: string): string {
  if (mime === 'image/jpeg') return 'jpg';
  if (mime === 'image/png') return 'png';
  if (mime === 'image/webp') return 'webp';
  return 'bin';
}

export function FeedbackForm() {
  const navigate = useNavigate();
  const location = useLocation();

  const [bodyMd, setBodyMd] = useState('');
  const [tags, setTags] = useState<ReadonlySet<FeedbackTag>>(() => new Set());
  const [customTag, setCustomTag] = useState('');
  const [submitState, setSubmitState] = useState<FeedbackFormSubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Phase 4 Step 2 — photos + screenshot state.
  const [attachedPhotos, setAttachedPhotos] = useState<AttachedPhoto[]>([]);
  const [photoUploadError, setPhotoUploadError] = useState<string | null>(null);
  const [screenshotEnabled, setScreenshotEnabled] = useState(false);
  const [screenshotPreviewUrl, setScreenshotPreviewUrl] = useState<string | null>(null);
  const [screenshotMockKey, setScreenshotMockKey] = useState<string | null>(null);
  const [screenshotError, setScreenshotError] = useState<string | null>(null);

  // Refs для unmount cleanup (закрытие над текущими preview URLs).
  const photosRef = useRef<AttachedPhoto[]>([]);
  const screenshotPreviewRef = useRef<string | null>(null);
  useEffect(() => {
    photosRef.current = attachedPhotos;
  }, [attachedPhotos]);
  useEffect(() => {
    screenshotPreviewRef.current = screenshotPreviewUrl;
  }, [screenshotPreviewUrl]);

  // Load draft once on mount.
  useEffect(() => {
    const draft = loadDraft();
    if (!draft) return;
    setBodyMd(draft.bodyMd);
    setTags(new Set(draft.tags));
    if (draft.customTag !== undefined) {
      setCustomTag(draft.customTag);
    }
  }, []);

  // Debounced save on changes (1000ms).
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (saveTimerRef.current !== null) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      saveDraft({
        bodyMd,
        tags: Array.from(tags),
        customTag: customTag.trim() || undefined,
      });
    }, DRAFT_SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimerRef.current !== null) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [bodyMd, tags, customTag]);

  // Cleanup all object URLs on unmount (memory leak prevention).
  useEffect(() => {
    return () => {
      for (const photo of photosRef.current) {
        URL.revokeObjectURL(photo.previewUrl);
      }
      if (screenshotPreviewRef.current !== null) {
        URL.revokeObjectURL(screenshotPreviewRef.current);
      }
    };
  }, []);

  const handleTagToggle = useCallback((tag: FeedbackTag) => {
    setTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
      }
      return next;
    });
  }, []);

  const handlePhotosAdd = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      setPhotoUploadError(null);

      // Validate + createObjectURL ВНЕ setAttachedPhotos updater — иначе
      // React 18 strict-mode double-invocation удвоит createObjectURL.
      // Read current state through closure: setAttachedPhotos получит финальный
      // массив, не зависящий от prev.
      const slotsRemaining = MAX_PHOTOS - attachedPhotos.length;
      if (slotsRemaining <= 0) {
        setPhotoUploadError(`Максимум ${MAX_PHOTOS} фото`);
        return;
      }

      const fileArray = Array.from(files).slice(0, slotsRemaining);
      const validated: AttachedPhoto[] = [];
      for (const file of fileArray) {
        if (!(ALLOWED_PHOTO_MIME as readonly string[]).includes(file.type)) {
          for (const v of validated) URL.revokeObjectURL(v.previewUrl);
          setPhotoUploadError('Поддерживаются только JPG, PNG, WebP');
          return;
        }
        if (file.size > MAX_PHOTO_SIZE_BYTES) {
          for (const v of validated) URL.revokeObjectURL(v.previewUrl);
          setPhotoUploadError('Максимальный размер фото — 5 MB');
          return;
        }
        validated.push({
          blob: file,
          mockKey: `mock-feedback-photo-${genUuid()}.${getExtensionFromMime(file.type)}`,
          previewUrl: URL.createObjectURL(file),
        });
      }

      if (files.length > slotsRemaining) {
        setPhotoUploadError(`Максимум ${MAX_PHOTOS} фото — лишние отброшены`);
      }
      setAttachedPhotos((prev) => [...prev, ...validated]);
    },
    [attachedPhotos],
  );

  const handlePhotoRemove = useCallback((index: number) => {
    setAttachedPhotos((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
    setPhotoUploadError(null);
  }, []);

  const handleScreenshotToggle = useCallback(async (enabled: boolean) => {
    setScreenshotError(null);
    if (!enabled) {
      if (screenshotPreviewRef.current !== null) {
        URL.revokeObjectURL(screenshotPreviewRef.current);
      }
      setScreenshotEnabled(false);
      setScreenshotPreviewUrl(null);
      setScreenshotMockKey(null);
      return;
    }
    try {
      const blob = await captureScreenshot({ consented: true });
      setScreenshotEnabled(true);
      setScreenshotPreviewUrl(URL.createObjectURL(blob));
      setScreenshotMockKey(`mock-feedback-screenshot-${genUuid()}.png`);
    } catch {
      setScreenshotError('Не удалось захватить скриншот. Попробуйте ещё раз');
      setScreenshotEnabled(false);
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    setSubmitState('loading');
    setErrorMessage(null);

    const state = location.state as { blockId?: string; screenId?: string } | null;
    const blockId = state?.blockId ?? 'unknown';
    const screenId = state?.screenId ?? 'unknown';

    const payload: FeedbackCreate = {
      kind: 'screen_improvement',
      bodyMd,
      tags: Array.from(tags),
      customTag: customTag.trim() || undefined,
      blockId,
      screenId,
      screenshotR2Key: screenshotMockKey,
      attachedPhotosR2Keys: attachedPhotos.map((p) => p.mockKey),
    };

    const parsed = FeedbackScreenImprovementSchema.safeParse(payload);
    if (!parsed.success) {
      setErrorMessage('Проверьте поля формы');
      setSubmitState('error');
      return;
    }

    try {
      await apiClient.feedback.create(parsed.data);
      // Отменяем pending debounced autosave ДО clearDraft: иначе таймер может
      // сработать после очистки и переписать только что удалённый черновик
      // (B-221 — race между saveTimerRef и clearDraft при unmount через navigate).
      if (saveTimerRef.current !== null) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      clearDraft();
      navigate('/feedback/sent');
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'UNAUTHORIZED') {
        setErrorMessage('Сессия истекла. Войдите снова');
      } else {
        setErrorMessage('Произошла ошибка. Попробуйте ещё раз');
      }
      setSubmitState('error');
    }
  }, [bodyMd, tags, customTag, attachedPhotos, screenshotMockKey, location.state, navigate]);

  return (
    <FeedbackFormView
      bodyMd={bodyMd}
      tags={tags}
      customTag={customTag}
      submitState={submitState}
      errorMessage={errorMessage}
      attachedPhotos={attachedPhotos}
      photoUploadError={photoUploadError}
      screenshotEnabled={screenshotEnabled}
      screenshotPreviewUrl={screenshotPreviewUrl}
      screenshotError={screenshotError}
      onBodyMdChange={setBodyMd}
      onTagToggle={handleTagToggle}
      onCustomTagChange={setCustomTag}
      onPhotosAdd={handlePhotosAdd}
      onPhotoRemove={handlePhotoRemove}
      onScreenshotToggle={handleScreenshotToggle}
      onSubmit={handleSubmit}
    />
  );
}
